import { useEffect, useId, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Modal } from "../ui/Modal";
import type { BackendModuleKey } from "../../lib/backendModuleKeys";
import type { CreateAccessRequestBody } from "../../lib/accessRequests";
import { getCatalogEntry } from "../../lib/moduleCatalog";
import { formatCatalogRequiresNote } from "../../lib/moduleCatalogRequires";
import { accessRequestCreateErrorMessage } from "../../lib/accessRequestErrors";
import { useAccessRequestMutations } from "../../hooks/useAccessRequests";

type RequestAccessDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  moduleKey?: BackendModuleKey;
  buildingId?: number;
};

export function RequestAccessDialog({
  isOpen,
  onClose,
  moduleKey,
  buildingId,
}: RequestAccessDialogProps) {
  const { t } = useTranslation();
  const messageId = useId();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [message, setMessage] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { createMutation } = useAccessRequestMutations();
  const catalogEntry = moduleKey ? getCatalogEntry(moduleKey) : undefined;
  const requiredGrants = catalogEntry?.requires ?? [];
  const requiresNote =
    requiredGrants.length > 0 ? formatCatalogRequiresNote(requiredGrants, t) : null;

  useEffect(() => {
    if (!isOpen) return;
    const timer = window.setTimeout(() => textareaRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [isOpen]);

  const handleClose = () => {
    setMessage("");
    setSubmitError(null);
    onClose();
  };

  const handleSubmit = async () => {
    const trimmed = message.trim();
    const body: CreateAccessRequestBody =
      buildingId != null
        ? { target_type: "building", target_building_id: buildingId, message: trimmed }
        : {
            target_type: "module",
            module_key: moduleKey ?? "payments",
            message: trimmed,
          };

    setSubmitError(null);
    try {
      await createMutation.mutateAsync(body);
      handleClose();
    } catch (error: unknown) {
      setSubmitError(accessRequestCreateErrorMessage(error, t));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t("accessRequests.dialog.title")}
      description={t("accessRequests.dialog.description")}
      size="sm"
    >
      <div className="space-y-4">
        {requiresNote ? (
          <p
            className="text-sm text-gray-600 dark:text-gray-300"
            data-testid="access-request-requires-note"
          >
            {requiresNote}
          </p>
        ) : null}
        <div>
          <label htmlFor={messageId} className="label">
            {t("accessRequests.dialog.messageLabel")}
          </label>
          <textarea
            ref={textareaRef}
            id={messageId}
            name="access_request_message"
            rows={4}
            maxLength={500}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="input w-full resize-y min-h-[5rem]"
            placeholder={t("accessRequests.dialog.messagePlaceholder")}
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {t("accessRequests.dialog.messageHint")}
          </p>
        </div>
        {submitError ? (
          <p
            className="text-sm text-red-600 dark:text-red-400"
            role="alert"
            data-testid="access-request-submit-error"
          >
            {submitError}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={handleClose}>
            {t("common.cancel", { defaultValue: "Anulează" })}
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => void handleSubmit()}
            disabled={createMutation.isPending}
            aria-busy={createMutation.isPending}
          >
            {createMutation.isPending ? (
              <Loader2 className="w-5 h-5 animate-spin" aria-hidden />
            ) : null}
            {t("accessRequests.requestButton")}
          </button>
        </div>
      </div>
    </Modal>
  );
}
